import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { api, errMsg } from "../lib/api";
import { Sparkles } from "lucide-react";
import { NAV } from "../lib/nav";
import { HELP, tabsFor } from "../lib/bookTabs";
import { useCurrentBook } from "../lib/currentBook";
import { useAuth } from "../context/AuthContext";

export function QuickSearch() {
  const [open, setOpen] = useState(false);
  const [books, setBooks] = useState([]);
  const [q, setQ] = useState("");
  const [ans, setAns] = useState(null);
  const nav = useNavigate();
  const { user } = useAuth();
  const cur = useCurrentBook();
  useEffect(() => {
    const key = (e) => { if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setOpen((o) => !o); } };
    const ev = () => setOpen(true);
    window.addEventListener("keydown", key); window.addEventListener("open-search", ev);
    return () => { window.removeEventListener("keydown", key); window.removeEventListener("open-search", ev); };
  }, []);
  useEffect(() => { if (open) api.get("/books").then((r) => setBooks(r.data)).catch(() => {}); }, [open]);
  const go = (to) => { setOpen(false); nav(to); };
  const isAdmin = ["owner", "admin"].includes(user?.role);
  const navItems = NAV.filter((n) => (!n.admin || isAdmin) && (!n.owner || user?.role === "owner"));
  const ask = async () => {
    setAns({ loading: true });
    const tabs = Object.fromEntries((cur ? tabsFor(cur.format).map(([k]) => k) : Object.keys(HELP).filter((k) => navItems.some((n) => n.id === k))).map((k) => [k, HELP[k] || k]));
    try { const { data } = await api.post("/help/ask", { question: q, book_id: cur?.id || null, tabs, pages: Object.fromEntries(navItems.map((n) => [n.id, n.label])) }); setAns(data); }
    catch (e) { setAns({ answer: errMsg(e), links: [] }); }
  };
  const follow = (l) => {
    if (l.type === "page") return go(navItems.find((n) => n.id === l.key)?.to || "/app");
    if (cur) return go(`/app/books/${cur.id}?tab=${l.key}`);
    const n = navItems.find((x) => x.id === l.key);
    go(n ? n.to : "/app");
  };
  return (
    <CommandDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setQ(""); setAns(null); } }}>
      <CommandInput value={q} onValueChange={(v) => { setQ(v); setAns(null); }} placeholder="Search, or ask a question like “how do I make a cover?”" data-testid="quick-search-input" />
      {ans && (
        <div className="px-4 py-3 border-b border-slate-800 space-y-2" data-testid="help-answer">
          {ans.loading ? <p className="text-sm text-slate-400 animate-pulse">Thinking…</p> : (<>
            <p className="text-sm text-slate-200" data-testid="help-answer-text">{ans.answer}</p>
            <div className="flex flex-wrap gap-2">{(ans.links || []).map((l, i) => <button key={i} type="button" onClick={() => follow(l)} className="text-xs px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-500 text-white" data-testid={`help-link-${i}`}>{l.label || l.key} →</button>)}</div>
          </>)}
        </div>
      )}
      <CommandList data-testid="quick-search-list">
        <CommandEmpty>Nothing found.</CommandEmpty>
        {q.trim().length >= 4 && (
          <CommandGroup heading="Help">
            <CommandItem value={`ask ${q}`} onSelect={ask} data-testid="help-ask"><Sparkles size={14} className="mr-2 text-amber-300" />Ask the help assistant: “{q.trim()}”</CommandItem>
          </CommandGroup>
        )}
        {cur && (
          <CommandGroup heading={`In “${cur.title}”`}>
            {tabsFor(cur.format).map(([k, l]) => <CommandItem key={k} value={`tab ${l}`} onSelect={() => go(`/app/books/${cur.id}?tab=${k}`)} data-testid={`qs-tab-${k}`}>{l}<span className="ml-2 text-[11px] text-slate-500 truncate">{HELP[k]}</span></CommandItem>)}
          </CommandGroup>
        )}
        <CommandGroup heading="Your projects">
          {books.slice(0, 50).map((b) => <CommandItem key={b.id} value={`book ${b.title} ${b.author || ""}`} onSelect={() => go(`/app/books/${b.id}`)} data-testid={`qs-book-${b.id}`}>{b.title}<span className="ml-2 text-[11px] text-slate-500">{b.format || "novel"}</span></CommandItem>)}
        </CommandGroup>
        <CommandGroup heading="Pages">
          {navItems.map((n) => <CommandItem key={n.id} value={`page ${n.label}`} onSelect={() => go(n.to)} data-testid={`qs-page-${n.id}`}><n.icon size={14} className="mr-2" />{n.label}</CommandItem>)}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
